import React, { useEffect, useState, useRef } from "react";
import { Button, Card, CardBody, Col, Row, Spinner, Label, Input } from "reactstrap";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "../../Common/Components/Button/CustomButton";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import { ADMIN_PATHS } from "../../config";
import CostBearerModel from "../Models/Response/CostBearerModel";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { useTranslation } from "react-i18next";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { CustomSubmitButton } from "../../Common/Components/Input/CustomSubmitButton";
import AdminRequestCostBearerUpdate from "../Models/Request/AdminRequestCostBearerUpdate";
import AdminResponseOrgTypeCompleteModel from "../../Role/Models/Response/AdminResponseOrgTypeCompleteModel";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";

const AdminCostBearerUpdate = () => {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id') ?? "";
    const navigate = useNavigate();
    const isMountedRef = useRef(true);
    const isMobile = CheckMobile(998);
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    useEffect(() => {
        setPageTitle(t('costBearer.update'));
        setPageTitleKey('costBearer.update');
        setOnBack(() => { navigate(ADMIN_PATHS.ADMIN_COST_BEARER); });
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

    const [costBearer, setCostBearer] = useState(new CostBearerModel());
    const [editCostBearer, setEditCostBearer] = useState(new AdminRequestCostBearerUpdate());
    const [displaySpinner, setDisplaySpinner] = useState(false);
    const [responseDelete, setResponseDelete] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});

    // States for organization types
    const [allOrgTypes, setAllOrgTypes] = useState([]);
    const [orgTypesWithCostBearer, setOrgTypesWithCostBearer] = useState([]);
    const [loadingOrgTypes, setLoadingOrgTypes] = useState(false);
    const [updatingOrgTypes, setUpdatingOrgTypes] = useState(false);
    const [hoveredOrgType, setHoveredOrgType] = useState(null);
    const [selectedOrgTypes, setSelectedOrgTypes] = useState([]);

    // MODALS
    const [displayDeleteCostBearerModal, setDisplayDeleteCostBearerModal] = useState(false);
    const [displayReactivateModal, setDisplayReactivateModal] = useState(false);

    let permission = false;
    if (CheckUserPermission("system.setting.cost-bearer.update") || CheckUserPermission("system.setting.cost-bearer") || CheckUserPermission("system") || CheckUserPermission("system.setting")) {
        permission = true;
    }

    // track mount/unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // GET COST BEARER (only when id changes)
    useEffect(() => {
        if (!id) return;

        const handleApiResponse = (apiResponse) => {
            if (!isMountedRef.current) return;
            setCostBearer(new CostBearerModel(apiResponse));
            setEditCostBearer(new AdminRequestCostBearerUpdate(apiResponse));
        };

        const fetchData = async () => {
            try {
                const apiCallOptions = {
                    method: "get",
                    url: `/costbearer?id=${id}`
                };
                await ApiCall(apiCallOptions, handleApiResponse);
            } catch (error) {
                console.log('error fetching cost bearer', error);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // GET ALL ORG TYPES - run once on mount
    useEffect(() => {
        const fetchAllOrgTypes = async () => {
            if (!isMountedRef.current) return;
            setLoadingOrgTypes(true);
            try {
                const apiCallOptions = {
                    method: "get",
                    url: "/role/admin/type/org/list-complete"
                };
                await ApiCall(apiCallOptions, (response) => {
                    if (!isMountedRef.current) return;
                    if (response && Array.isArray(response)) {
                        const orgTypesArray = response.map(item => new AdminResponseOrgTypeCompleteModel(item));
                        setAllOrgTypes(orgTypesArray);
                    } else {
                        setAllOrgTypes([]);
                    }
                    setLoadingOrgTypes(false);
                });
            } catch (error) {
                console.log('error fetching org types', error);
                if (isMountedRef.current) setLoadingOrgTypes(false);
            }
        };
        fetchAllOrgTypes();
    }, []);

    // GET ORG TYPES THAT HAVE THIS COST BEARER (runs only when costBearer.name changes)
    useEffect(() => {
        if (!costBearer?.name) {
            // reset if no name
            setOrgTypesWithCostBearer([]);
            setSelectedOrgTypes([]);
            return;
        }

        const fetchOrgTypesByCostBearer = async () => {
            try {
                const encoded = encodeURIComponent(costBearer.name);
                const apiCallOptions = {
                    method: "get",
                    url: `/role/admin/type/org/list-by-costbearer?costBearerName=${encoded}`
                };
                await ApiCall(apiCallOptions, (response) => {
                    if (!isMountedRef.current) return;
                    if (response && Array.isArray(response)) {
                        const types = response.map(org => org.type).filter(Boolean);
                        setOrgTypesWithCostBearer(types);
                        setSelectedOrgTypes(types);
                    } else {
                        setOrgTypesWithCostBearer([]);
                        setSelectedOrgTypes([]);
                    }
                });
            } catch (error) {
                console.log('error fetching org types by cost bearer', error);
                if (isMountedRef.current) {
                    setOrgTypesWithCostBearer([]);
                    setSelectedOrgTypes([]);
                }
            }
        };

        fetchOrgTypesByCostBearer();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costBearer?.name]); // only depend on the name

    const handleChangeCostBearer = (e, name) => {
        let value;
        if (e && e.target) {
            value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        }

        setEditCostBearer((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));

        // clear field error while editing
        setFieldErrors(prev => {
            const copy = { ...prev };
            delete copy[name];
            return copy;
        });
    };

    useEffect(() => {
        if (errorMessage !== '') {
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            setDisplaySpinner(false);
            setErrorMessage('');
            setTimeout(() => { CustomReload(); }, 4800);
        }
    }, [errorMessage, t]);

    // --- helper: check existence using 'list-all' fallback; excludes current id
    const checkExists = async (field, value) => {
        if (!value) return false;
        const trimmed = (value || '').trim().toLowerCase();
        // fallback query endpoint (adjust if you have /exists endpoints)
        try {
            const response = await new Promise((resolve, reject) => {
                ApiCall({
                    method: "get",
                    url: `/costbearer/list-all?query=${encodeURIComponent(value)}`
                }, resolve, reject);
            });

            const items = (response && (response.items || response)) || [];
            if (!Array.isArray(items)) return false;

            // exclude the current record by id
            const currentId = editCostBearer?.id || costBearer?.id || id || null;

            return items.some(it => {
                if (!it) return false;
                const compare = field === 'name' ? (it.name || '') : (it.value || '');
                if (!compare) return false;
                if (currentId && it.id === currentId) return false; // same record -> ignore
                return compare.trim().toLowerCase() === trimmed;
            });
        } catch (e) {
            console.warn("checkExists fallback failed", e);
            return false;
        }
    };

    // onBlur handlers to show inline errors
    const handleBlurName = async () => {
        setTouched(prev => ({ ...prev, name: true }));
        // first local simple check
        if (!editCostBearer.name || editCostBearer.name.trim() === "") {
            setFieldErrors(prev => ({ ...prev, name: 'validationMessage.fieldRequired' }));
            return;
        }
        try {
            const exists = await checkExists('name', editCostBearer.name);
            if (exists) {
                setFieldErrors(prev => ({ ...prev, name: 'validationMessage.nameExists' }));
            } else {
                setFieldErrors(prev => { const c = { ...prev }; delete c.name; return c; });
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleBlurValue = async () => {
        setTouched(prev => ({ ...prev, value: true }));
        if (!editCostBearer.value || editCostBearer.value.trim() === "") {
            setFieldErrors(prev => ({ ...prev, value: 'validationMessage.fieldRequired' }));
            return;
        }
        // ensure numeric-only at the UI level is enforced elsewhere; here only uniqueness
        try {
            const exists = await checkExists('value', editCostBearer.value);
            if (exists) {
                setFieldErrors(prev => ({ ...prev, value: 'validationMessage.kbarExists' }));
            } else {
                setFieldErrors(prev => { const c = { ...prev }; delete c.value; return c; });
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleValidation = async () => {
        setDisplaySpinner(true);

        const schema = AdminRequestCostBearerUpdate.getAttributes(null, true);

        try {
            // yup full validation
            await schema.validate(editCostBearer, { abortEarly: false });

            // check duplicates server-side / via search (exclude current id)
            const [nameExists, valueExists] = await Promise.all([
                checkExists('name', editCostBearer.name),
                checkExists('value', editCostBearer.value)
            ]);

            if (nameExists) {
                setFieldErrors(prev => ({ ...prev, name: 'validationMessage.nameExists' }));
                setTouched(prev => ({ ...prev, name: true }));
                setDisplaySpinner(false);
                return;
            }
            if (valueExists) {
                setFieldErrors(prev => ({ ...prev, value: 'validationMessage.kbarExists' }));
                setTouched(prev => ({ ...prev, value: true }));
                setDisplaySpinner(false);
                return;
            }

            const CostBearerSendHandler = (apiResponse) => {
                if (!isMountedRef.current) return;
                if (apiResponse) {
                    setCostBearer(apiResponse);
                    setDisplaySpinner(false);
                    FlashMessage(t('validationMessage.costBearerUpdate'), 'flash-messageSuccess');
                    setTimeout(() => {
                        navigate(`${ADMIN_PATHS.ADMIN_COST_BEARER}`);
                    }, 2000);
                }
            };

            const apiCallOptions = {
                method: "put",
                url: "/costbearer",
                data: editCostBearer,
            };
            try {
                await ApiCall(apiCallOptions, CostBearerSendHandler, setErrorMessage);
            } catch (error) {
                console.log(error);
                FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
                setDisplaySpinner(false);
            }
        } catch (err) {
            // yup validation errors -> show inline
            const newErrors = {};
            if (err && err.inner && err.inner.length > 0) {
                err.inner.forEach(e => {
                    if (e.path) newErrors[e.path] = e.message;
                });
            } else if (err && err.path) {
                newErrors[err.path] = err.message;
            }
            setFieldErrors(newErrors);
            const newTouched = { ...touched };
            Object.keys(newErrors).forEach(k => { newTouched[k] = true; });
            setTouched(newTouched);
            setDisplaySpinner(false);
        }
    };

    // HANDLE MODAL DELETE
    const HandleDeleteCostBearer = async () => {
        if (id !== "") {
            const apiCallOptions = {
                method: "delete",
                url: `/costbearer?id=${id}`,
            };
            try {
                await ApiCall(apiCallOptions, setResponseDelete);
                setDisplayDeleteCostBearerModal(false);
            } catch (error) {
                console.log({ error });
            }
        }
    };

    useEffect(() => {
        if (responseDelete !== null) {
            CustomReload(ADMIN_PATHS.ADMIN_COST_BEARER);
        }
    }, [responseDelete]);

    // HANDLE CHECKBOX CHANGE (controlado por React)
    const handleCheckboxChange = (e, orgType) => {
        const checked = e.target.checked;
        setSelectedOrgTypes(prev => {
            const prevArr = prev || [];
            if (checked) {
                const next = Array.from(new Set([...prevArr, orgType]));
                return next;
            } else {
                return prevArr.filter(type => type !== orgType);
            }
        });
    };

    // HANDLE SUBMIT ORGANIZATIONS (una sola llamada bulk)
    const handleSubmitOrganizations = async () => {
        setUpdatingOrgTypes(true);

        try {
            const current = orgTypesWithCostBearer || [];
            const selected = selectedOrgTypes || [];

            const toAdd = selected.filter(type => !current.includes(type));
            const toRemove = current.filter(type => !selected.includes(type));

            if (toAdd.length === 0 && toRemove.length === 0) {
                FlashMessage(t('validationMessage.noChanges') || 'No changes to apply', 'flash-messageInfo');
                setUpdatingOrgTypes(false);
                return;
            }

            const payload = {
                costBearerName: costBearer.name,
                orgTypesToAdd: toAdd,
                orgTypesToRemove: toRemove
            };

            const response = await new Promise((resolve, reject) => {
                ApiCall({
                    method: "post",
                    url: "/role/admin/type/org/update-costbearers-bulk",
                    data: payload
                }, resolve, reject);
            });

            let orgTypesArray = [];

            if (Array.isArray(response)) {
                orgTypesArray = response;
            } else if (response && Array.isArray(response.orgTypes)) {
                orgTypesArray = response.orgTypes;
            } else if (response && Array.isArray(response.items)) {
                orgTypesArray = response.items;
            } else if (response && typeof response === 'object') {
                orgTypesArray = Object.values(response).find(v => Array.isArray(v)) || [];
            }

            const updatedTypes = (orgTypesArray || []).map(o => (o.type || o.Type || o.name || o.Id || o.id)).filter(Boolean);
            const finalTypes = updatedTypes.length > 0 ? updatedTypes : selected;

            if (isMountedRef.current) {
                setOrgTypesWithCostBearer(finalTypes);
                setSelectedOrgTypes(finalTypes);
            }

            FlashMessage(t('costBearer.organizationsUpdated'), 'flash-messageSuccess');
            setTimeout(() => {
                CustomReload();
            }, 3000);
        } catch (error) {
            console.error("Error updating organizations (bulk):", error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        } finally {
            if (isMountedRef.current) setUpdatingOrgTypes(false);
        }
    };

    const modalContentDeleteCostBearerModal = (
        <div>
            {`${t('validationMessage.modalVar')}?`}
        </div>
    );

    const modalFooterDeleteCostBearerModal = (
        <>
            <Button className="box-green border-0" onClick={HandleDeleteCostBearer}>
                {t('button.delete')}
            </Button>
            <Button className="box-red border-0" onClick={() => setDisplayDeleteCostBearerModal(false)}>
                {t('button.cancel')}
            </Button>
        </>
    );

    const modalContentReactivate = (
        <div>
            {`${t('validationMessage.modalReactivate')}`}
        </div>
    );

    const modalFooterReactivate = (
        <>
            <Button
                className="box-green border-0"
                onClick={async () => {
                    try {
                        setDisplaySpinner(true);
                        const apiCallOptions = {
                            method: "put",
                            url: `/costbearer/reactivate?id=${id}`,
                        };
                        await ApiCall(apiCallOptions, (apiResponse) => {
                            if (!isMountedRef.current) return;
                            setEditCostBearer(apiResponse);
                            setDisplaySpinner(false);
                            FlashMessage(t('validationMessage.costBearerReactivated'), 'flash-messageSuccess');
                        });
                        setDisplayReactivateModal(false);
                    } catch (error) {
                        console.log(error);
                        FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
                        setDisplaySpinner(false);
                    }
                }}
            >
                {t('button.reactivate')}
            </Button>
            <Button
                className="box-red border-0"
                onClick={() => setDisplayReactivateModal(false)}
            >
                {t('button.cancel')}
            </Button>
        </>
    );

    const renderFieldErrorAbove = (fieldName) => {
        const err = fieldErrors[fieldName];
        const translated = err ? (typeof err === 'string' ? t(err) : err) : '';
        return (
            <div className={`error tooltip-custom-normal position-absolute ${err ? 'opacity-1' : 'opacity-0'}`}
                style={{ color: '#f56e6e' }}>
                {translated}
            </div>
        );
    };

    return (
        <>
            {/* Modal de eliminar */}
            <CustomModal
                isOpen={displayDeleteCostBearerModal}
                toggle={() => setDisplayDeleteCostBearerModal(!displayDeleteCostBearerModal)}
                body={modalContentDeleteCostBearerModal}
                footer={modalFooterDeleteCostBearerModal}
            />

            {/* Modal de reactivar */}
            <CustomModal
                isOpen={displayReactivateModal}
                toggle={() => setDisplayReactivateModal(!displayReactivateModal)}
                body={modalContentReactivate}
                footer={modalFooterReactivate}
            />

            {!isMobile && (
                <Col lg={12}>
                    <CustomButton
                        text={t('button.back')}
                        icon={'bx bx-chevron-left'}
                        buttonSize={'small'}
                        customClasses={'ms-3 mb-3'}
                        onClick={() => navigate(ADMIN_PATHS.ADMIN_COST_BEARER)}
                    />
                </Col>
            )}

            <Row className="pb-5 px-0 mx-0">
                <Col lg={6} className="px-0 mx-0 mb-3 pe-lg-2">
                    <Card className="card-backend p-4">
                        <Row>
                            <Col lg={12}>
                                <h2 className="rokkitt-font">{t('costBearer.update')}</h2>
                            </Col>
                            <Col lg={12}>
                                <Row>
                                    <Col lg={12}>
                                        <div className={'mb-3 position-relative'} style={{ paddingTop: '10px' }}>
                                            {renderFieldErrorAbove('value')}
                                            <CustomInput
                                                model={AdminRequestCostBearerUpdate}
                                                attribute='value'
                                                data={editCostBearer.value}
                                                customChange={(e) => handleChangeCostBearer(e, 'value')}
                                                inputProps={{ id: 'value', onBlur: handleBlurValue }}
                                            />
                                        </div>
                                    </Col>
                                    <Col lg={12}>
                                        <div className={'mb-3 position-relative'} style={{ paddingTop: '10px' }}>
                                            {renderFieldErrorAbove('name')}
                                            <CustomInput
                                                model={AdminRequestCostBearerUpdate}
                                                attribute='name'
                                                data={editCostBearer.name}
                                                customChange={(e) => handleChangeCostBearer(e, 'name')}
                                                inputProps={{ id: 'name', onBlur: handleBlurName }}
                                            />
                                        </div>
                                    </Col>
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label>{t('costBearer.message')}</label>
                                            <textarea
                                                className="form-control"
                                                value={editCostBearer.message}
                                                onChange={(e) => handleChangeCostBearer(e, "message")}
                                                style={{ resize: "vertical", minHeight: "60px", width: "100%", overflow: "auto" }}
                                                rows={2}
                                            />
                                        </div>
                                    </Col>
                                    <Col lg={12}>
                                        <div className="form-check mb-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="isActiveCheckbox"
                                                checked={!!editCostBearer.includedInAGI}
                                                onChange={(e) =>
                                                    setEditCostBearer((prev) => ({
                                                        ...prev,
                                                        includedInAGI: e.target.checked,
                                                    }))
                                                }
                                            />
                                            <label className="form-check-label" htmlFor="isActiveCheckbox">
                                                {t("costBearer.includedInAGILabel")}
                                            </label>
                                        </div>
                                    </Col>

                                    <Col lg={12} className="text-end pt-5">
                                        {id !== "" &&
                                            (editCostBearer.deleted
                                                ? (
                                                    <Button
                                                        className="rounded-2 box-green border-0 me-2"
                                                        onClick={() => setDisplayReactivateModal(true)}
                                                    >
                                                        <i className="bx bx-reset fs-5 pe-1" /> {t("button.reactivate")}
                                                    </Button>
                                                )
                                                : (
                                                    <Button
                                                        className="rounded-2 box-red border-0 me-2"
                                                        onClick={() => setDisplayDeleteCostBearerModal(true)}
                                                    >
                                                        <i className="bx bx-x-circle fs-5 pe-1" /> {t("button.delete")}
                                                    </Button>
                                                )
                                            )
                                        }

                                        {displaySpinner && (
                                            <>
                                                {t('validationMessage.sendingCostBearer')} <Spinner color='success' className="me-3" />
                                            </>
                                        )}

                                        <CustomSubmitButton
                                            text={t('button.update')}
                                            id='submitCostBearer'
                                            icon='bx bx-save pe-2'
                                            customClick={async () => {
                                                await handleValidation();
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Right Column - Organizations */}
                <Col lg={6} className="px-0 mx-0 ps-lg-2">
                    {permission && (
                        <Card className="card-backend mb-3">
                            <CardBody>
                                <h5 className="mb-3 text-green fw-bold">{t('costBearer.relatedOrgs')}</h5>
                                {loadingOrgTypes ? (
                                    <div className="text-center py-4">
                                        <Spinner color='success' />
                                    </div>
                                ) : (
                                    <>
                                        <Row>
                                            {allOrgTypes.map((orgType) => {
                                                const sanitizedId = (orgType.type || '').replace(/\s+/g, '-');
                                                const isChecked = selectedOrgTypes.includes(orgType.type);
                                                return (
                                                    <div className="col-6" key={orgType.type}>
                                                        <Label className="d-flex align-items-start">
                                                            <Input
                                                                id={`orgType-${sanitizedId}`}
                                                                type="checkbox"
                                                                name="orgType"
                                                                value={orgType.type}
                                                                disabled={updatingOrgTypes}
                                                                onChange={(e) => handleCheckboxChange(e, orgType.type)}
                                                                className="mt-1"
                                                                style={{ flexShrink: 0 }}
                                                                checked={isChecked}
                                                            />
                                                            <span className="ms-2" style={{ wordBreak: 'break-word' }}>
                                                                {orgType.type}
                                                            </span>
                                                        </Label>
                                                    </div>
                                                );
                                            })}
                                        </Row>
                                        {hoveredOrgType && allOrgTypes.find(org => org.type === hoveredOrgType)?.permissions && (
                                            <div className="mt-1 mb-3 bg-orange-light rounded border border-warning text-warning p-2 text-center">
                                                <h6>{t('role.permissions')}</h6>
                                                <span>
                                                    {allOrgTypes.find(org => org.type === hoveredOrgType)?.permissions.join(', ')}
                                                </span>
                                            </div>
                                        )}
                                        <div className="text-end">
                                            <span className="me-3">
                                                {updatingOrgTypes ? (
                                                    <>
                                                        {t('validationMessage.assigningOrganizations')} <Spinner color='success' />
                                                    </>
                                                ) : null}
                                            </span>
                                            <CustomSubmitButton
                                                id="submitOrganizations"
                                                text={t("button.assign")}
                                                icon="bx bx-send pe-2"
                                                customClick={async () => {
                                                    await handleSubmitOrganizations();
                                                }}
                                                disabled={updatingOrgTypes}
                                            />
                                        </div>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    )}
                </Col>
            </Row>
        </>
    );
};

export default AdminCostBearerUpdate;